// Solo play: runs a local World in the browser against bots, turns it into a Scene for the renderer,
// and posts each finished life to /api/solo/result (the Worker re-checks it and awards the points).
// Every life first asks /api/solo/start for a signed single-use ticket; the result carries it, so the
// Worker can bound the life's length and refuse replays.
import { World } from '../shared/sim';
import {
  BOT_COUNT, EXCITE_AT, GOLDEN_BONUS, POWERUP_TYPES, RANK_POINTS, rad,
  type DifficultyId, type PowerUpType, type SkinId,
} from '../shared/config';
import {
  CF_BOOST, CF_GHOST, CF_GOLDEN, CF_MAGNET, CF_SHIELD, CF_SPEED,
  type Player, type PlayerInput, type Scene, type SceneCell, type SimEvent,
} from '../shared/types';
import {
  ApiError, soloResultAward, soloResultBeacon, soloStart, type Profile, type SoloResultBody, type SoloResultPost,
} from './api';

export type SoloResult = SoloResultBody;
export interface SoloOptions {
  name: string; skin: SkinId; difficulty: DifficultyId;
  excite: () => boolean; // saved points >= EXCITE_AT (my mass is checked here)
  onReport?: (profile: Profile, result: SoloResult, awarded: number) => void; // server reply to a posted result
  report?: boolean; // post results to the Worker (default true)
  rng?: () => number;
}

const LEADERBOARD_ROWS = 10; // same as the online BOARD message
const WARM_UP_SECONDS = 20; // head start so bots have some size when you join
const FX_FLAG: Record<PowerUpType, number> = { ghost: CF_GHOST, shield: CF_SHIELD, magnet: CF_MAGNET, speed: CF_SPEED };
const TICKET_TRIES = 3, TICKET_RETRY_MS = 1500; // a ticket lost to a network blip is asked for again
// The Worker caps a claimed aliveSec at the ticket's age + 2 s. The ticket's age is at least the time since
// it reached this page, so a claim capped at that + 1.9 s always passes, even when the ticket came late.
// (Sim time never runs ahead of real time: every frame's dt is clamped, a hidden tab doesn't step.)
const TICKET_SLACK_S = 1.9;

interface Ticket { ticket: string; at: number } // at: local clock (ms) when it arrived
const clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
async function fetchTicket(): Promise<Ticket | null> {
  for (let i = 1; ; i++) {
    try {
      const t = await soloStart();
      return { ticket: t.ticket, at: clock() };
    } catch (e) {
      const st = e instanceof ApiError ? e.status : -1;
      if (i >= TICKET_TRIES || !(st === 0 || st >= 500)) return null; // locked, rate limited, ...: retrying won't help
      await sleep(TICKET_RETRY_MS * i);
    }
  }
}
// the body the Worker gets: the stats, aliveSec capped by the ticket's age, and the ticket. Without a
// ticket (the start request failed) the stats go alone and the Worker decides.
function withTicket(r: SoloResult, t: Ticket | null): SoloResultPost {
  if (!t) return { ...r };
  const age = Math.max(0, (clock() - t.at) / 1000);
  return { ...r, aliveSec: Math.min(r.aliveSec, Math.floor((age + TICKET_SLACK_S) * 10) / 10), ticket: t.ticket };
}

// points the Worker should award for a life (it may award less if the life fails its plausibility checks)
export const expectedPoints = (r: SoloResult) => (RANK_POINTS[r.bestRank] ?? 0) + GOLDEN_BONUS * r.goldenKills;

export class SoloSession {
  readonly world: World;
  readonly myId: number;
  result: SoloResult | null = null; // the last finished life
  lastReport: Promise<Profile | null> | null = null; // the last result post (null reply = failed)

  private goldenKills = 0;
  private reported = false; // this life's result has been posted
  private life = 0;
  private ticket: Promise<Ticket | null> = Promise.resolve(null); // this life's ticket request
  private ticketNow: Ticket | null = null; // ...once it has arrived (a beacon can't wait for it)
  private readonly onReport: SoloOptions['onReport'];
  private readonly reportOn: boolean;

  constructor(opts: SoloOptions) {
    this.onReport = opts.onReport;
    this.reportOn = opts.report !== false;
    // power-ups and events: saved points, or 700+ mass in this match
    const excite = () => opts.excite() || this.myMass() >= EXCITE_AT;
    this.world = new World({ mode: 'solo', difficulty: opts.difficulty, botCount: BOT_COUNT, excite, rng: opts.rng });
    this.world.warmUp(WARM_UP_SECONDS);
    this.myId = this.world.addPlayer(opts.name, opts.skin).id;
    this.startTicket();
  }

  get player(): Player | undefined { return this.world.players.find(p => p.id === this.myId); }
  get alive(): boolean { return !!this.player?.alive; }

  step(dt: number, input: PlayerInput): void {
    const p = this.player;
    if (p) {
      const live = p.alive;
      this.world.setInput(this.myId, {
        tx: Number.isFinite(input.tx) ? input.tx : p.tx, ty: Number.isFinite(input.ty) ? input.ty : p.ty,
        split: live && input.split, eject: live && input.eject, boost: live && input.boost,
      });
    }
    this.world.step(Math.min(0.05, Math.max(0, dt || 0)));
  }

  // Everything in the world (solo has no interest management). Fresh objects every call.
  scene(): Scene {
    const w = this.world, now = w.now, me = this.player;
    const cells: SceneCell[] = [], myCells: SceneCell[] = [];
    let myMass = 0;
    for (const p of w.players) {
      if (!p.alive) continue;
      let flags = (p.golden ? CF_GOLDEN : 0) | (p.boosting ? CF_BOOST : 0);
      if (p.fx) for (const k of POWERUP_TYPES) if ((p.fx[k] ?? 0) > now) flags |= FX_FLAG[k];
      const mine = p.id === this.myId;
      for (const c of p.cells) {
        if (c.m <= 0) continue;
        const sc: SceneCell = { id: c.id, ownerId: p.id, x: c.x, y: c.y, r: rad(c.m), mass: c.m, skin: p.skin, name: p.name, flags };
        cells.push(sc);
        if (mine) { myCells.push(sc); myMass += c.m; }
      }
    }
    const myFx: Scene['myFx'] = {};
    if (me?.alive && me.fx) for (const k of POWERUP_TYPES) {
      const left = (me.fx[k] ?? 0) - now;
      if (left > 0) myFx[k] = left;
    }
    const pellets: Scene['pellets'] = [];
    for (const f of w.pellets) if (!f.dead) pellets.push({ id: f.id, x: f.x, y: f.y, r: f.r, color: f.color, kind: f.kind, ph: f.ph ?? 0 });
    const board = w.leaderboard();
    const myRank = board.findIndex(r => r.id === this.myId);
    const ev = w.event;
    return {
      now, myId: this.myId, alive: !!me?.alive,
      myCells, myMass, myFx, cells, pellets,
      viruses: w.viruses.map(v => ({ id: v.id, x: v.x, y: v.y, r: rad(v.m) })),
      powerups: w.powerups.map(u => ({ id: u.id, type: u.type, x: u.x, y: u.y, ph: u.ph, born: u.born })),
      leaderboard: board.slice(0, LEADERBOARD_ROWS).map(r => ({ name: r.name, mass: r.mass, me: r.id === this.myId })),
      myRank,
      event: ev ? { type: ev.type, remaining: Math.max(0, ev.until - now), dur: ev.until - ev.start } : null,
      online: false, roomName: '', humans: 1,
    };
  }

  // Drains the sim's events for sounds/toasts; also tracks my golden kills and posts my result when I die.
  drainEvents(): SimEvent[] {
    const evs = this.world.drainEvents();
    for (const e of evs) {
      if (e.t === 'kill' && e.killer === this.myId && e.golden) this.goldenKills++;
      else if (e.t === 'death' && e.player === this.myId) {
        this.result = this.makeResult(e.bestRank, e.peakMass, e.aliveSec, e.kills, e.goldenKills ?? this.goldenKills);
        this.post(this.result);
      }
    }
    return evs;
  }

  // "Play again": a new life in the same world
  respawn(): void {
    this.world.respawn(this.myId);
    this.goldenKills = 0;
    this.reported = false;
    this.result = null;
    this.startTicket();
  }

  // Back to the lobby: a life cut short while alive still earns its points (as in the original).
  leave(): Promise<Profile | null> {
    const p = this.player;
    if (p?.alive && !this.reported) {
      this.result = this.makeResult(p.bestRank, p.peakMass, this.world.now - p.spawnedAt, p.kills, p.goldenKills ?? this.goldenKills);
      this.post(this.result);
    }
    return this.lastReport ?? Promise.resolve(null);
  }

  // The page is going away (tab hidden, or closed) during a live life: posts the life so far with
  // sendBeacon. That closes the life for points, exactly like leaving: if the player comes back and plays
  // on, nothing is posted again (a second post would pay the same rank twice). False if nothing was sent.
  beacon(): boolean {
    const p = this.player, t = this.ticketNow;
    if (!this.reportOn || this.reported || !p?.alive || !t) return false; // no ticket yet: nothing the Worker would take
    this.result = this.makeResult(p.bestRank, p.peakMass, this.world.now - p.spawnedAt, p.kills, p.goldenKills ?? this.goldenKills);
    this.reported = true;
    this.lastReport = Promise.resolve(null); // no reply comes back; the lobby refresh shows the new total
    return soloResultBeacon(withTicket(this.result, t));
  }

  private myMass(): number {
    const p = this.player;
    if (!p?.alive) return 0;
    let m = 0;
    for (const c of p.cells) m += c.m;
    return m;
  }
  private makeResult(bestRank: number, peakMass: number, aliveSec: number, kills: number, goldenKills: number): SoloResult {
    const int = (v: number, hi: number) => (Number.isFinite(v) ? Math.max(0, Math.min(hi, Math.floor(v))) : 0);
    return {
      bestRank: Number.isFinite(bestRank) ? int(bestRank, 99) : 99, // 99 = never ranked
      peakMass: int(peakMass, 1e7), aliveSec: Number.isFinite(aliveSec) ? Math.max(0, Math.round(aliveSec * 10) / 10) : 0,
      kills: int(kills, 1e4), goldenKills: int(goldenKills, 100),
    };
  }
  // a new life: ask for its ticket (the backdrop world, report:false, never touches the network)
  private startTicket(): void {
    const life = ++this.life;
    this.ticketNow = null;
    if (!this.reportOn) { this.ticket = Promise.resolve(null); return; }
    this.ticket = fetchTicket().then(t => { if (life === this.life) this.ticketNow = t; return t; });
  }
  private post(r: SoloResult): void {
    if (this.reported) return;
    this.reported = true;
    if (!this.reportOn) return;
    // a life that ends before its ticket arrives waits for it (the request has its own timeout)
    this.lastReport = this.ticket
      .then(t => soloResultAward(withTicket(r, t)))
      .then(({ profile, awarded }) => { this.onReport?.(profile, r, awarded); return profile; })
      .catch((e: unknown) => {
        // offline or rejected: the lobby refresh shows the server's real total
        if (!(e instanceof ApiError)) console.error(e);
        return null;
      });
  }
}
